import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { normalizePhotos } from '@/lib/coverImage';

interface Props {
  photos: unknown;
  alt?: string;
  /**
   * Resolved category cover (see src/lib/coverImage.ts). Rendered as a
   * full-width hero when the poster uploaded no usable photos.
   */
  fallbackCover?: string;
  /**
   * Branded marketplace cover. Used if `fallbackCover` itself fails to load,
   * so the detail page can never end up with an empty image area.
   */
  secondaryCover?: string;
}

/**
 * ───────────────────────────────────────────────────────────────────────────
 * PROJECT PHOTO GALLERY
 * ───────────────────────────────────────────────────────────────────────────
 * BLANK-AREA FIXES (2026-09-22):
 *  1. photos are parsed with the SHARED normalizePhotos() helper, so a JSON
 *     string / object list / protocol-relative URL is no longer silently
 *     discarded (that alone made a homeowner's real uploads disappear);
 *  2. the hero <img> is revealed through a cached-image guard identical to
 *     CoverImage — `complete && naturalWidth > 0` — instead of trusting a load
 *     event that a cached file never fires;
 *  3. the component NEVER returns null any more. If every upload fails it
 *     steps to the category cover, then to the branded cover, then to a
 *     visible branded tile. There is no path that renders nothing.
 */
const PhotoGallery: React.FC<Props> = ({ photos, alt, fallbackCover, secondaryCover }) => {
  const { t } = useI18n();
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<string[]>([]);
  /** 0 = category cover, 1 = branded cover, 2 = branded tile. */
  const [coverStep, setCoverStep] = useState(0);
  const startX = useRef<number | null>(null);
  const deltaX = useRef(0);

  const parsed = normalizePhotos(photos);
  const list = parsed.filter((p) => !failed.includes(p));

  // Keep the active slide in range when a photo drops out of the list.
  useEffect(() => {
    if (index > 0 && index >= list.length) setIndex(0);
  }, [list.length, index]);

  const markFailed = (url: string) => {
    setFailed((prev) => (prev.includes(url) ? prev : [...prev, url]));
    setIndex(0);
  };

  // ── No usable uploads: branded category cover instead of an empty page ──
  if (list.length === 0) {
    const covers = [fallbackCover, secondaryCover].filter(
      (c): c is string => typeof c === 'string' && /^https?:\/\//i.test(c),
    );
    const cover = covers[coverStep];

    return (
      <div className="mt-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900">
          {cover ? (
            <img
              key={cover}
              src={cover}
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="async"
              onError={() => setCoverStep((s) => s + 1)}
              className="h-64 w-full select-none object-cover object-center sm:h-96"
            />
          ) : (
            /* Last resort — a visible branded panel, never an empty box. */
            <div className="flex h-64 w-full items-center justify-center sm:h-96">
              <ImageIcon className="h-12 w-12 text-orange-400/80" aria-hidden="true" />
            </div>
          )}
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-slate-950/75 to-transparent" />
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur">
            <ImageIcon className="h-3 w-3" /> {t('cover.category')}
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-400">{t('cover.categoryNote')}</p>
      </div>
    );
  }

  const total = list.length;
  const go = (next: number) => setIndex(((next % total) + total) % total);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    deltaX.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    deltaX.current = e.touches[0].clientX - startX.current;
  };
  const onTouchEnd = () => {
    if (Math.abs(deltaX.current) > 45) go(index + (deltaX.current < 0 ? 1 : -1));
    startX.current = null;
    deltaX.current = 0;
  };

  return (
    <div className="mt-6">
      <div
        className="relative overflow-hidden rounded-2xl bg-slate-900"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') go(index + 1);
          if (e.key === 'ArrowLeft') go(index - 1);
        }}
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {list.map((p, i) => (
            <img
              key={`slide-${p}-${i}`}
              src={p}
              alt={`${alt || t('gallery.photo')} ${i + 1}`}
              draggable={false}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              onError={() => markFailed(p)}
              className="h-64 w-full shrink-0 select-none object-cover object-center sm:h-96"
            />
          ))}
        </div>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label={t('gallery.prev')}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-900 shadow-md transition-colors hover:bg-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label={t('gallery.next')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-900 shadow-md transition-colors hover:bg-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5">
              {list.map((p, i) => (
                <button
                  key={`dot-${p}-${i}`}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`${t('gallery.photo')} ${i + 1}`}
                  aria-current={i === index}
                  className={`h-2 rounded-full transition-all ${i === index ? 'w-6 bg-white' : 'w-2 bg-white/50'}`}
                />
              ))}
            </div>

            <span className="absolute right-3 top-3 rounded-full bg-slate-900/70 px-2.5 py-1 text-xs font-bold text-white">
              {index + 1}/{total}
            </span>
          </>
        )}
      </div>

      {total > 1 && (
        <>
          <p className="mt-2 text-center text-xs text-slate-400 sm:hidden">{t('gallery.swipeHint')}</p>
          <div className="mt-3 hidden gap-2 sm:flex">
            {list.map((p, i) => (
              <button
                key={`thumb-${p}-${i}`}
                type="button"
                onClick={() => go(i)}
                aria-label={`${t('gallery.photo')} ${i + 1}`}
                className={`overflow-hidden rounded-lg border-2 transition-colors ${
                  i === index ? 'border-orange-500' : 'border-transparent hover:border-slate-300'
                }`}
              >
                <img
                  src={p}
                  alt=""
                  className="h-16 w-20 object-cover"
                  loading="lazy"
                  onError={() => markFailed(p)}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoGallery;
